import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";

export interface Citation {
  uri: string;
  title: string;
}

interface CitationTooltipProps {
  citations: Citation[];
  citationNumber: number;
}

const CitationTooltip: React.FC<CitationTooltipProps> = ({ citations, citationNumber }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const tooltipWidth = 320;
      const tooltipHeight = 400;
      const spacing = 8;
      const edgePadding = 16;
      
      // Calculate horizontal position (centered under badge)
      let left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
      
      // Adjust horizontal position if it would go off screen
      if (left < edgePadding) {
        left = edgePadding;
      } else if (left + tooltipWidth > window.innerWidth - edgePadding) {
        left = window.innerWidth - tooltipWidth - edgePadding;
      }
      
      // Calculate vertical position (prefer below)
      let top = rect.bottom + spacing;
      
      // If tooltip would go off bottom of viewport, offset it upward
      if (top + tooltipHeight > window.innerHeight - edgePadding) {
        // Position it so the bottom edge is at the viewport bottom minus padding
        top = window.innerHeight - tooltipHeight - edgePadding;
      }
      
      // Ensure tooltip doesn't go off top of viewport
      if (top < edgePadding) {
        top = edgePadding;
      }
      
      setPosition({ top, left });
    }
  }, []);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsOpen(true);
  };

  const handleMouseLeave = () => {
    // Delay closing to allow mouse to move to tooltip
    closeTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 200);
  };

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);
      
      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Extract first domain from title (e.g., "cdc.gov" or "Example Article - domain.com")
  const extractDomain = (title: string): string => {
    // If title is just a domain (e.g., "cdc.gov"), return it
    if (/^[\w-]+\.[\w-]+(\.\w+)?$/.test(title)) {
      return title;
    }
    // Otherwise try to extract domain from end of title
    const match = title.match(/[\w-]+\.[\w-]+(\.\w+)?$/);
    return match ? match[0] : title;
  };
  
  const firstDomain = citations[0]?.title 
    ? extractDomain(citations[0].title)
    : '';

  return (
    <>
      <span
        ref={buttonRef}
        className="citation-badge"
        data-citation={citationNumber}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={() => setIsOpen(!isOpen)}
      >
        {citationNumber}
      </span>

      {isOpen && createPortal(
        <div
          ref={tooltipRef}
          className="citation-tooltip-anchor"
          style={{ top: `${position.top}px`, left: `${position.left}px` }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="citation-tooltip-panel">
            {/* Header */}
            <div className="citation-tooltip-header">
              <div className="citation-source-count">
                {citations.length} {citations.length === 1 ? 'source' : 'sources'}
              </div>
              <div className="citation-domain-preview">
                {firstDomain}
              </div>
            </div>

            {/* Sources List */}
            <div className="citation-sources-list">
              {citations.map((citation, idx) => {
                const domain = citation.title
                  ? extractDomain(citation.title)
                  : '';

                return (
                  <a
                    key={idx}
                    href={citation.uri}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="citation-source-link"
                  >
                    <div className="citation-source-card">
                      <div className="citation-source-card-inner">
                        <div className="citation-source-index">
                          {idx + 1}
                        </div>
                        <div className="citation-source-body">
                          <div className="citation-source-title">
                            {citation.title || 'Untitled'}
                          </div>
                          <div className="citation-source-domain">
                            {domain}
                          </div>
                        </div>
                      </div>
                    </div>
                  </a>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default CitationTooltip;
